import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api';
import { addParamsToUrl } from 'utils';
import { Child, PaginationResponse } from 'types';

import { apiQuery } from '../../query';
import { authApi, profileTagType } from '../auth';

import {
  ListRequest,
  ListResponse,
  CreateRequest,
  UpdateRequest,
  DetailsRequest,
  ChildrenResponse,
} from './types';

const tagTypes = ['CHILDREN'];

export const childrenApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'childrenApi',
  endpoints: (build) => ({
    details: build.query<Child, DetailsRequest>({
      providesTags: tagTypes,
      transformResponse: (response: ChildrenResponse) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.CHILDREN_DETAILS.replace(':id', id),
      }),
    }),

    list: build.query<PaginationResponse<Child>, ListRequest>({
      providesTags: tagTypes,
      transformResponse: (response: ListResponse) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CHILDREN, params),
      }),
    }),

    create: build.mutation<Child, CreateRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CHILDREN,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(authApi.util.invalidateTags([profileTagType]));
        } catch (error) {
          console.error(error);
        }
      },
    }),

    update: build.mutation<Child, UpdateRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.CHILDREN_DETAILS.replace(':id', id),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(authApi.util.invalidateTags([profileTagType]));
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useCreateMutation,
  useUpdateMutation,
} = childrenApi;

export type { ListRequest, CreateRequest };
